import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { DataTable, EmptyState, PageHeader, StatusChip, useNotify } from '@routes-plan/ui';
import { User } from '../../../models/user';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import PermissionButton from '../../../components/common/PermissionButton';
import StandardModal from '../../../components/modales/StandardModal';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { isSuperAdminUser } from '../../../utils/authScope';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { AdminPage, ContentCard, FilterRow, RowAction, RowActions, apiError, useConfirm } from '../shared/AdminUi';

type RowId = string | number;

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

interface UserRow {
    id: number;
    idNumber: string;
    username: string;
    fullName: string;
    role: string;
    enabled: boolean;
}

const UserList: React.FC<ShowAlerts> = () => {
    const t = useT();
    const notify = useNotify();
    const [confirm, confirmDialog] = useConfirm();
    setPageTitle('users.pageTitle');
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const {
        canCreate,
        canRead,
        canUpdate,
        canDelete,
        canToggleEnabled,
        authUserData,
        isLoadingPermissions,
    } = useResourcePermissions('/admin/users');

    const [associationsOpen, setAssociationsOpen] = useState(false);
    const [associationsLoading, setAssociationsLoading] = useState(false);
    const [associationsOffices, setAssociationsOffices] = useState<Office[]>([]);
    const [associationsRoutes, setAssociationsRoutes] = useState<Route[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);
    const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
    const [officeId, setOfficeId] = useState<string>('');
    const [routeId, setRouteId] = useState<string>('');
    const [officesLoaded, setOfficesLoaded] = useState(false);
    const isSuperAdmin = isSuperAdminUser(authUserData);

    const openAssociationsModal = async (userId: RowId) => {
        const numericId = typeof userId === 'string' ? Number(userId) : userId;
        if (!Number.isFinite(numericId)) return;

        setAssociationsOpen(true);
        setAssociationsLoading(true);
        setAssociationsOffices([]);
        setAssociationsRoutes([]);

        try {
            // Oficinas asociadas
            const { data } = await axios.get(`users/${numericId}/offices`);
            const offices = Array.isArray(data?.offices) ? data.offices : [];
            const usersOffice = Array.isArray(data?.users) ? data.users : [];
            const officeIdsFromUsers = usersOffice
                .map((u: any) => Number(u?.officeId))
                .filter((n: number) => Number.isFinite(n));
            const officeIds = officeIdsFromUsers.length > 0 ? officeIdsFromUsers : offices.map((o: any) => Number(o?.id)).filter((n: number) => Number.isFinite(n));

            setAssociationsOffices(offices);

            // Cobros asociados (por cada oficina)
            if (officeIds.length > 0) {
                const results = await Promise.all(
                    officeIds.map((oid: number) =>
                        axios
                            .get(`users/${numericId}/offices/${oid}/routes`)
                            .catch(() => null)
                    )
                );

                const routesFromAllOffices: Route[] = [];
                results.forEach((r: any) => {
                    const selectedRoutes = Array.isArray(r?.data?.selectedRoutes) ? r.data.selectedRoutes : [];
                    routesFromAllOffices.push(...selectedRoutes);
                });

                // deduplicar por id del cobro
                const uniqueById = new Map<number, Route>();
                routesFromAllOffices.forEach((c: Route) => {
                    const cid = Number((c as any)?.id);
                    if (Number.isFinite(cid)) uniqueById.set(cid, c);
                });

                setAssociationsRoutes(Array.from(uniqueById.values()));
            }
        } catch (error) {
            console.error('Error loading user associations:', error);
            notify.error(t('users.assignmentsLoadError'));
        } finally {
            setAssociationsLoading(false);
        }
    };

    useEffect(() => {
        if (isLoadingPermissions) {
            return;
        }

        const fetchOffices = async () => {
            try {
                const officesUrl = isSuperAdmin ? 'offices/all' : 'offices';
                const officesResponse = await axios.get(officesUrl);
                const officeOptions = Array.isArray(officesResponse?.data?.data)
                    ? officesResponse.data.data
                    : [];

                setOffices(officeOptions);

                if (isSuperAdmin) {
                    setOfficeId('');
                    return;
                }

                if (officeOptions.length > 0) {
                    setOfficeId(String(officeOptions[0].id));
                }
            } catch (error) {
                console.error('Error loading offices: ', error);
                setOffices([]);
                setOfficeId('');
            } finally {
                setOfficesLoaded(true);
            }
        };

        fetchOffices();
    }, [isLoadingPermissions, isSuperAdmin]);

    useEffect(() => {
        if (!officesLoaded) {
            return;
        }

        if (!isSuperAdmin && offices.length > 0 && !officeId) {
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get('users', {
                    params: {
                        ...(officeId ? { officeId } : {}),
                        ...(routeId ? { routeId } : {}),
                    },
                });
                setUsers(data.data || []);
            } catch (error) {
                notify.error(t('common.loadError'));
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [routeId, officeId, isSuperAdmin, offices.length, officesLoaded]);

    useEffect(() => {
        const fetchRoutesByOffice = async () => {
            if (!officeId) {
                setOfficeRoutes([]);
                setRouteId('');
                return;
            }

            try {
                const { data } = await axios.get(`offices/${officeId}/routes`);
                const routes = Array.isArray(data) ? data : [];
                setOfficeRoutes(routes);

                if (isSuperAdmin) {
                    if (routeId && !routes.some((route: Route) => String(route.id) === String(routeId))) {
                        setRouteId('');
                    }
                    return;
                }

                if (routes.length === 0) {
                    setRouteId('');
                    return;
                }

                const currentRouteAllowed = routes.some(
                    (route: Route) => String(route.id) === String(routeId)
                );
                if (!currentRouteAllowed) {
                    setRouteId(String(routes[0].id));
                }
            } catch (error) {
                console.error('Error loading routes: ', error);
                setOfficeRoutes([]);
                setRouteId('');
            }
        };

        fetchRoutesByOffice();
    }, [routeId, isSuperAdmin, officeId]);

    const handleDeleteUser = async (row: UserRow) => {
        if (!canDelete) return;
        const ok = await confirm({ title: t('users.confirmDeleteTitle', { name: row.fullName || row.username }), message: t('users.confirmDelete') });
        if (!ok) return;
        try {
            await axios.delete(`users/${row.id}`);
            setUsers((prev) => prev.filter((user: User) => Number(user.id) !== row.id));
            notify.success(t('users.deletedOk'));
        } catch (error: any) {
            notify.error(apiError(error, t('users.deleteError')));
        }
    };

    const handleToggleUserState = async (id: RowId, checked: boolean) => {
        if (!canToggleEnabled) return;

        const numericId = typeof id === 'string' ? Number(id) : id;
        if (!Number.isFinite(numericId)) return;

        try {
            await axios.put('users/state', {
                users: [{ id: numericId, state: checked ? 1 : 0 }],
            });
            setUsers((prev) =>
                prev.map((user) =>
                    Number(user.id) === numericId
                        ? Object.assign(new User(), user, { state: checked ? 1 : 0 })
                        : user
                )
            );
            notify.success(checked ? t('users.enabledOk') : t('users.disabledOk'));
        } catch (error: any) {
            notify.error(apiError(error, t('users.stateUpdateError')));
        }
    };

    const rows: UserRow[] = users.map((user: User) => ({
        id: Number(user.id),
        idNumber: String(user.idNumber ?? ''),
        username: user.username ?? '',
        fullName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
        role: user.role?.name ?? '',
        enabled: Number((user as any).state ?? 1) === 1,
    }));

    const addButton = (
        <PermissionButton
            allowed={canCreate}
            deniedTooltip={t('common.noPermissionCreate')}
            variant="contained"
            startIcon={<AddIcon />}
            component={NavLink}
            to="/admin/users/new"
        >
            {t('users.newTitle')}
        </PermissionButton>
    );

    return (
        <AdminPage>
            <PageHeader title={t('users.title')} description={t('users.subtitle')} actions={addButton} />
            <ContentCard>
                <FilterRow sx={{ mb: 2 }}>
                    <TextField
                        id="users-office-filter"
                        select
                        size="small"
                        label={t('users.office')}
                        value={offices.length ? officeId : ''}
                        onChange={(event) => setOfficeId(String(event.target.value))}
                        SelectProps={{ displayEmpty: isSuperAdmin }}
                        InputLabelProps={{ shrink: isSuperAdmin || !!officeId }}
                        fullWidth
                    >
                        {isSuperAdmin && <MenuItem value="">{t('users.allOffices')}</MenuItem>}
                        {offices.map((office: Office) => (
                            <MenuItem key={office.id} value={String(office.id)}>
                                {office.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        id="users-route-filter"
                        select
                        size="small"
                        label={t('users.route')}
                        value={officeRoutes.length ? routeId : ''}
                        onChange={(event) => setRouteId(String(event.target.value))}
                        disabled={!officeId}
                        SelectProps={{ displayEmpty: isSuperAdmin }}
                        InputLabelProps={{ shrink: isSuperAdmin || !!routeId }}
                        fullWidth
                    >
                        {isSuperAdmin && <MenuItem value="">{t('users.allRoutes')}</MenuItem>}
                        {officeRoutes.map((route: Route) => (
                            <MenuItem key={route.id} value={String(route.id)}>
                                {route.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </FilterRow>
                <DataTable<UserRow>
                    rows={rows}
                    loading={loading}
                    getRowId={(r) => r.id}
                    ariaLabel={t('users.title')}
                    searchable
                    searchPlaceholder={t('users.searchPlaceholder')}
                    exportFileName={t('users.exportFileName')}
                    initialSort={{ columnId: 'fullName', direction: 'asc' }}
                    columns={[
                        { id: 'fullName', header: t('users.fullName'), value: (r) => r.fullName, sortable: true },
                        { id: 'username', header: t('common.username'), value: (r) => r.username, sortable: true },
                        { id: 'idNumber', header: t('common.idNumber'), value: (r) => r.idNumber, sortable: true, hideBelow: 'md' },
                        { id: 'role', header: t('common.role'), value: (r) => r.role, sortable: true, hideBelow: 'sm' },
                        {
                            id: 'state',
                            header: t('common.status'),
                            value: (r) => (r.enabled ? t('common.enabled') : t('common.disabled')),
                            render: (r) =>
                                canToggleEnabled ? (
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                        <Switch
                                            size="small"
                                            checked={r.enabled}
                                            onChange={(event) => handleToggleUserState(r.id, event.target.checked)}
                                            inputProps={{ 'aria-label': `${t('common.status')} ${r.fullName}` }}
                                        />
                                        <StatusChip label={r.enabled ? t('common.enabled') : t('common.disabled')} tone={r.enabled ? 'success' : 'neutral'} />
                                    </Box>
                                ) : (
                                    <StatusChip label={r.enabled ? t('common.enabled') : t('common.disabled')} tone={r.enabled ? 'success' : 'neutral'} />
                                ),
                            sortable: true,
                        },
                    ]}
                    rowActions={(row) => (
                        <RowActions>
                            <RowAction
                                label={`${t('users.viewAssignments')}: ${row.fullName}`}
                                icon={<AccountTreeOutlinedIcon fontSize="small" />}
                                allowed={canRead}
                                deniedTooltip={t('users.noPermissionViewAssignments')}
                                onClick={() => openAssociationsModal(row.id)}
                            />
                            <RowAction
                                label={`${t('common.edit')} ${row.fullName}`}
                                icon={<EditOutlinedIcon fontSize="small" />}
                                allowed={canUpdate}
                                deniedTooltip={t('common.noPermissionEdit')}
                                to={`/admin/users/${row.id}/edit`}
                            />
                            <RowAction
                                label={`${t('common.delete')} ${row.fullName}`}
                                icon={<DeleteOutlineIcon fontSize="small" />}
                                color="error"
                                allowed={canDelete}
                                deniedTooltip={t('common.noPermissionDelete')}
                                onClick={() => handleDeleteUser(row)}
                            />
                        </RowActions>
                    )}
                    emptyState={
                        <EmptyState
                            icon={<PeopleAltOutlinedIcon />}
                            title={t('users.emptyTitle')}
                            description={t('users.emptyDescription')}
                            action={canCreate ? addButton : undefined}
                        />
                    }
                />
            </ContentCard>

            <StandardModal
                open={associationsOpen}
                onClose={() => setAssociationsOpen(false)}
                title={t('users.assignmentsTitle')}
                maxWidth={520}
                loading={associationsLoading}
                actions={
                    <>
                        <span />
                        <Button variant="contained" onClick={() => setAssociationsOpen(false)}>
                            {t('common.close')}
                        </Button>
                    </>
                }
            >
                {associationsLoading ? null : (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('users.assignmentsHint')}
                        </Typography>
                        <Box>
                            <Typography variant="subtitle2">{t('users.officesCount', { count: associationsOffices.length })}</Typography>
                            <List dense disablePadding>
                                {associationsOffices.length > 0 ? (
                                    associationsOffices.map((o: Office) => (
                                        <ListItem key={o.id} disableGutters>
                                            <ListItemText primary={o.name} />
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem disableGutters>
                                        <ListItemText primary={t('users.noOfficesAssigned')} primaryTypographyProps={{ color: 'text.secondary' }} />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2">{t('users.routesCount', { count: associationsRoutes.length })}</Typography>
                            <List dense disablePadding>
                                {associationsRoutes.length > 0 ? (
                                    associationsRoutes.map((c: Route) => (
                                        <ListItem key={c.id} disableGutters>
                                            <ListItemText
                                                primary={c.name}
                                                secondary={c.office?.name ? t('users.officeOf', { name: c.office.name }) : undefined}
                                            />
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem disableGutters>
                                        <ListItemText primary={t('users.noRoutesAssigned')} primaryTypographyProps={{ color: 'text.secondary' }} />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    </Box>
                )}
            </StandardModal>
            {confirmDialog}
        </AdminPage>
    );
};

export default UserList;
